import { Component } from 'react';
import Loading from '../../components/Loading';
import { showMessage } from '../../helpers/showMessage';
import {
    confirmAiTrialUpgrade,
    getCurrentSubscription,
    getRegistrationInfo,
    previewSubscriptionChange,
} from './query';
import {
    AI_TRIAL_DAYS,
    FREE_AI_TRACKING_QUANTITY,
    addDays,
    addInterval,
    addMonths,
    buildBillingSummary,
    buildPriceItems,
    daysUntil,
    formatDate,
    formatMoney,
    intervalSuffix,
    resolveAiPrice,
    resolveCurrentPlanDisplay,
} from './helpers';
import {
    ArrowRightIcon,
    BulbIcon,
    CalendarIcon,
    CardIcon,
    ClockIcon,
    FileIcon,
    InfoIcon,
    LockIcon,
    RenewIcon,
    SparkleIcon,
} from './icons';

/**
 * "Review Your Subscription Update" — the page a legacy (old-system) user lands on when they
 * choose to try AI Tracking from the dashboard banner.
 *
 * It reuses the existing v2 subscription endpoints end to end:
 *   1. GET  /api/v2/accounts/subscription/         -> their current plan (projected onto the new
 *                                                     products; user_type is "traditional").
 *   2. GET  /api/v2/accounts/registration/         -> the catalogue, to resolve the monthly AI
 *                                                     Tracking price used for the cost preview.
 *   3. PATCH /api/v2/accounts/subscription/?preview=true -> the itemized invoice for the change:
 *                                                     one line per product, plus subtotal, discount,
 *                                                     total and amount-due totals.
 *   4. PATCH /api/v2/accounts/subscription/ with with_free_ai_tracking:true -> on confirm, the
 *                                                     backend migrates the user to the new system
 *                                                     and applies the same price_items the preview
 *                                                     was computed from.
 *
 * Steps 3 and 4 send an identical price_items list — the user's existing products plus AI Tracking
 * (see buildPriceItems) — so the quote and the charge can never diverge. AI Tracking must be in
 * that list: with_free_ai_tracking only stretches the item's trial from 14 to 30 days, it does not
 * add the product, and the API returns a 400 when the flag arrives without a matching price item.
 *
 * The user's own products never change here — only AI Tracking is added (free for 30 days), and
 * the billing date is reset to today, which is what produces the prorated credit shown below.
 */
class AiTrialUpgrade extends Component {
    constructor(props) {
        super(props);

        this.onConfirm = this.onConfirm.bind(this);
        this.onKeep = this.onKeep.bind(this);

        this.body = document.getElementsByTagName('body')[0];
        this.isDark = JSON.parse(localStorage.getItem('dark-mode'));
        if (localStorage.getItem('dark-mode') === 'true') {
            this.body.classList.add('dark-mode');
        }

        this.state = {
            loading: true,
            submitting: false,
            error: '',
            current: null,
            preview: null,
            aiPrice: null,
            priceItems: null,
        };
    }

    async componentDidMount() {
        await this.loadData();
    }

    async loadData() {
        try {
            this.setState({ loading: true, error: '' });

            // The subscription and the catalogue are independent, but the preview is not: it needs
            // the AI Tracking price_id out of the catalogue, so it has to wait for both.
            const [subResponse, infoResponse] = await Promise.all([
                getCurrentSubscription(),
                getRegistrationInfo(),
            ]);

            const current = await subResponse.json().catch(() => null);
            if (!subResponse.ok || !current) {
                this.setState({
                    loading: false,
                    error: current?.detail || 'We could not load your current subscription.',
                });
                return;
            }

            const info = infoResponse.ok ? await infoResponse.json().catch(() => null) : null;
            // Match AI Tracking to the interval the rest of the subscription bills at, so an annual
            // subscriber adds the annual AI price rather than a stray monthly item. `interval` on
            // the GET is the governing interval of the main products (AI Tracking excluded), which
            // is exactly the one to line up with.
            const aiPrice = resolveAiPrice(
                info?.active_plans,
                current.interval,
                current.interval_count,
            );
            if (!aiPrice) {
                this.setState({
                    loading: false,
                    error: 'AI Tracking is not available right now. Please try again later.',
                });
                return;
            }

            // Describe the subscription the user will end up on — their existing products plus AI
            // Tracking — the same way the custom-registration page does. The AI Tracking item must
            // be submitted explicitly: with_free_ai_tracking only sets the trial to 30 days, it does
            // not add the product, and the API rejects the flag without a matching price item.
            const priceItems = buildPriceItems(current.items, aiPrice);
            const previewResponse = await previewSubscriptionChange({
                price_items: priceItems,
                // with_free_ai_tracking: true,
            });

            // The preview is the authority on every amount shown; if it fails the page still renders
            // from the current items (see buildBillingSummary) rather than blocking the upgrade.
            const preview = previewResponse.ok
                ? await previewResponse.json().catch(() => null)
                : null;

            this.setState({ loading: false, current, preview, aiPrice, priceItems });
        } catch (err) {
            this.setState({
                loading: false,
                error: err?.message || 'Something went wrong while loading your subscription.',
            });
        }
    }

    async onConfirm() {
        try {
            this.setState({ submitting: true });
            // Commit exactly the price_items the preview was computed from, so what the user was
            // quoted is what they are charged.
            // The preview also tells us up front when committing would drop an existing conversion
            // coupon (requires_discount_loss_acknowledgement). The backend rejects the PATCH with
            // 'conversion_discount_loss_not_acknowledged' unless we echo that back, and the user has
            // already seen the warning banner and the resulting total below it.
            const response = await confirmAiTrialUpgrade({
                price_items: this.state.priceItems,
                with_free_ai_tracking: true,
                acknowledge_discount_loss: Boolean(
                    this.state.preview?.requires_discount_loss_acknowledgement,
                ),
            });
            const data = await response.json().catch(() => null);

            if (response.ok) {
                showMessage(
                    'Your subscription was updated and AI Tracking is now active.',
                    'success',
                );
                window.localStorage.removeItem('userData');
                window.location.href = '/ai-tracking/prompt-generator/';
                return;
            }

            this.setState({ submitting: false });
            showMessage(
                data?.detail || data?.message || 'We could not update your subscription.',
                'error',
            );
        } catch (err) {
            this.setState({ submitting: false });
            showMessage(err?.message || 'We could not update your subscription.', 'error');
        }
    }

    onKeep() {
        window.location.href = '/';
    }

    render() {
        const { loading, submitting, error, current, preview, aiPrice } = this.state;

        if (loading) {
            return (
                <div className="ai-trial-upgrade">
                    <div className="ai-trial-upgrade__loader">
                        <Loading />
                    </div>
                </div>
            );
        }

        const items = current?.items || [];
        if (error || items.length === 0) {
            return (
                <div className="ai-trial-upgrade">
                    <div className="ai-trial-upgrade__card ai-trial-upgrade__card--empty">
                        <h1 className="ai-trial-upgrade__title">Review Your Subscription Update</h1>
                        <p className="ai-trial-upgrade__subtitle">
                            {error || 'We could not find an active subscription to update.'}
                        </p>
                        <a className="ai-trial-upgrade__keep" href="/">
                            Back to dashboard
                        </a>
                    </div>
                </div>
            );
        }

        const summary = buildBillingSummary(current, preview, aiPrice);
        const { currency } = summary;

        const today = new Date();
        const periodEnd = current?.current_period_end ? new Date(current.current_period_end) : null;
        // Derived from the subscription's actual interval (monthly, yearly, ...) rather than
        // assuming a one-month cycle, so legacy annual plans get a correct period start too.
        const periodStart = periodEnd
            ? addInterval(periodEnd, current?.interval, current?.interval_count, -1)
            : null;
        const daysRemaining = daysUntil(periodEnd);
        const nextBillingDate = summary.nextPaymentDate
            ? new Date(summary.nextPaymentDate)
            : addMonths(today, 1);
        // The free AI Tracking trial always runs a fixed 30 days from today, independent of the
        // subscription's next billing date.
        const aiTrialEndsDate = addDays(today, AI_TRIAL_DAYS);

        // AI Tracking is free for its first 30 days, so it is netted out of every figure that
        // describes what the user pays for this period: the preview still itemizes it at its list
        // price, but nothing about it is charged today. It is quoted separately, as the price that
        // starts once the trial ends.
        const aiRecurringCents = summary.aiRecurringCents || 0;
        // recurringCents is the *new*-system list price (sum of the preview's non-proration
        // lines) — the products a legacy user is being moved to, not what they pay today.
        const recurringCents = summary.recurringCents - aiRecurringCents;
        const recurringText = formatMoney(recurringCents, currency);
        // The preview reports the invoice totals explicitly: amount_due_cents is what is actually
        // collected today (total minus any customer credit balance), so it drives every "charged
        // today" figure on the page.
        const totalText = formatMoney(summary.amountDueCents - aiRecurringCents, currency);
        // The new subscription's recurring price is the new list price less the transition
        // discount — correct regardless of whether today's invoice also carries a proration line
        // (unlike the invoice total, which folds in that one-time proration amount).
        const newPriceCents =
            summary.discountCents > 0 ? recurringCents - summary.discountCents : recurringCents;
        const newPriceText = formatMoney(newPriceCents, currency);
        const showWasPrice = summary.discountCents > 0;

        // The "Current Subscription" box must show what the user is actually paying today, which
        // for a legacy user is not reflected in `items` (projected onto new-system products).
        const currentPlan = resolveCurrentPlanDisplay(current);
        const currentPriceText = formatMoney(currentPlan.priceCents, currency);
        const newProductLabels = items.map((item) => item.product_name).filter(Boolean);
        const intervalText = intervalSuffix(current?.interval, current?.interval_count);
        // The AI item renews at its own price's interval, which is the subscription's whenever the
        // catalogue had a match and monthly when it did not — so it is read off aiPrice, not off
        // the subscription.
        const aiIntervalText = intervalSuffix(aiPrice?.interval, aiPrice?.interval_count);
        const aiAfterTrialText =
            aiPrice?.per_unit_cents != null
                ? formatMoney(aiPrice.per_unit_cents * FREE_AI_TRACKING_QUANTITY, currency)
                : '$40.00';

        return (
            <div className="ai-trial-upgrade">
                {submitting && (
                    <div className="ai-trial-upgrade__loader">
                        <Loading />
                    </div>
                )}
                <div className="ai-trial-upgrade__card">
                    {this.renderHeader()}
                    {this.renderInfoBanner(summary)}
                    {summary.requiresDiscountAcknowledgement && this.renderDiscountLossWarning()}
                    {this.renderComparison({
                        currentPriceText,
                        currentProductLabels: currentPlan.productLabels,
                        newProductLabels,
                        recurringText,
                        newPriceText,
                        showWasPrice,
                        intervalText,
                        periodStart,
                        periodEnd,
                        daysRemaining,
                        today,
                        aiAfterTrialText,
                        aiIntervalText,
                    })}
                    {this.renderSummary({
                        summary,
                        currency,
                        recurringText,
                        totalText,
                        aiRecurringCents,
                    })}
                    {this.renderTip(summary)}
                    {this.renderTiles({
                        today,
                        nextBillingDate,
                        recurringText,
                        aiTrialEndsDate,
                        aiAfterTrialText,
                        aiIntervalText,
                    })}
                    <p className="ai-trial-upgrade__legal">
                        By confirming, you authorize GDdash to update your subscription
                        {summary.creditCents > 0 ? ', apply your credit' : ''}, reset your billing
                        date, and charge {totalText} today.
                    </p>
                    {this.renderActions({ totalText, submitting })}
                </div>
            </div>
        );
    }

    renderHeader() {
        return (
            <div className="ai-trial-upgrade__header">
                <span className="ai-trial-upgrade__header-icon">
                    <SparkleIcon size={26} />
                </span>
                <div className="ai-trial-upgrade__header-text">
                    <h1 className="ai-trial-upgrade__title">Review Your Subscription Update</h1>
                    <p className="ai-trial-upgrade__subtitle">
                        Start your free 30-day AI Tracking trial and move to our updated
                        subscription plan today.
                    </p>
                </div>
            </div>
        );
    }

    // Only promise a prorated credit when the preview actually contains one — a subscription with
    // no unused time left produces an invoice with no proration lines at all.
    renderInfoBanner(summary) {
        return (
            <div className="ai-trial-upgrade__info">
                <span className="ai-trial-upgrade__info-icon">
                    <InfoIcon size={20} />
                </span>
                <p className="ai-trial-upgrade__info-text">
                    <strong>Your billing date will reset to today.</strong>{' '}
                    {summary.creditCents > 0
                        ? 'A prorated credit for the unused portion of your current subscription will be applied.'
                        : 'The exact amount charged today is broken down below.'}
                </p>
            </div>
        );
    }

    renderDiscountLossWarning() {
        return (
            <div className="ai-trial-upgrade__warning">
                <span className="ai-trial-upgrade__warning-icon">
                    <InfoIcon size={20} />
                </span>
                <p className="ai-trial-upgrade__warning-text">
                    <strong>A discount on your current subscription will be removed.</strong>{' '}
                    Confirming this update ends it — the amounts below already reflect that.
                </p>
            </div>
        );
    }

    renderProducts(labels) {
        return labels.map((label) => (
            <div className="ai-trial-upgrade__plan-product" key={label}>
                <span className="ai-trial-upgrade__plan-product-name">{label}</span>
            </div>
        ));
    }

    renderComparison({
        currentPriceText,
        currentProductLabels,
        newProductLabels,
        recurringText,
        newPriceText,
        showWasPrice,
        intervalText,
        periodStart,
        periodEnd,
        daysRemaining,
        today,
        aiAfterTrialText,
        aiIntervalText,
    }) {
        return (
            <div className="ai-trial-upgrade__compare">
                <div className="ai-trial-upgrade__plan">
                    <div className="ai-trial-upgrade__plan-head">
                        <FileIcon size={18} />
                        <span>Current Subscription</span>
                    </div>
                    <div className="ai-trial-upgrade__plan-products">
                        {this.renderProducts(currentProductLabels)}
                    </div>
                    <div className="ai-trial-upgrade__plan-price">
                        {currentPriceText}
                        <span>{intervalText}</span>
                    </div>
                    <div className="ai-trial-upgrade__plan-divider" />
                    <div className="ai-trial-upgrade__plan-meta">
                        <CalendarIcon size={16} />
                        <div>
                            <div className="ai-trial-upgrade__plan-meta-label">
                                Current billing period
                            </div>
                            {periodStart && periodEnd && (
                                <div className="ai-trial-upgrade__plan-meta-value">
                                    {formatDate(periodStart)} – {formatDate(periodEnd)}
                                </div>
                            )}
                            <div className="ai-trial-upgrade__plan-meta-sub">
                                {daysRemaining} days remaining
                            </div>
                        </div>
                    </div>
                </div>

                <div className="ai-trial-upgrade__compare-arrow">
                    <ArrowRightIcon size={22} />
                </div>

                <div className="ai-trial-upgrade__plan ai-trial-upgrade__plan--new">
                    <div className="ai-trial-upgrade__plan-head">
                        <FileIcon size={18} />
                        <span>New Subscription</span>
                    </div>
                    <div className="ai-trial-upgrade__plan-products">
                        {this.renderProducts(newProductLabels)}
                    </div>
                    <div className="ai-trial-upgrade__plan-price">
                        {showWasPrice && (
                            <span className="ai-trial-upgrade__plan-price-was">
                                {recurringText}
                            </span>
                        )}
                        {newPriceText}
                        <span>{intervalText}</span>
                    </div>
                    <div className="ai-trial-upgrade__plan-lock">
                        <LockIcon size={16} />
                        Price locked for 12 months
                    </div>
                    <div className="ai-trial-upgrade__plan-product ai-trial-upgrade__plan-product--ai">
                        <span className="ai-trial-upgrade__plan-product-name">+ AI Tracking</span>
                        <span className="ai-trial-upgrade__plan-product-note">
                            (1st month free, then {aiAfterTrialText} {aiIntervalText} after trial)
                        </span>
                    </div>
                    <div className="ai-trial-upgrade__plan-divider" />
                    <div className="ai-trial-upgrade__plan-meta">
                        <CalendarIcon size={16} />
                        <div>
                            <div className="ai-trial-upgrade__plan-meta-label">
                                New billing date
                            </div>
                            <div className="ai-trial-upgrade__plan-meta-value">
                                {formatDate(today)} (today)
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /**
     * "Today's Billing Summary" — a direct rendering of the preview invoice.
     *
     * The preview response now itemizes the invoice (one line per product, each flagged as a
     * proration or not) and reports subtotal / discount / total / amount due explicitly, so the
     * table mirrors it row for row instead of re-deriving totals in the browser. Subtotal, discount
     * and applied-balance rows only appear when they carry a value, keeping the simple case (one
     * charge, no coupon) as short as it was before.
     */
    renderSummary({ summary, currency, recurringText, totalText, aiRecurringCents = 0 }) {
        // AI Tracking comes back as its own preview line (we submit it in price_items), so it is
        // pulled out of the product rows and rendered with the "first 30 days" label instead of
        // Stripe's raw description. Its first 30 days are free, so the row is always $0 and the
        // line's own amount is netted out of the subtotal below rather than being shown.
        const chargeLines = summary.lines.filter((line) => !line.isProration && !line.isAiTracking);
        const prorationLines = summary.lines.filter((line) => line.isProration);
        // Subtotal is only worth a row when something sits between it and the total.
        const showSubtotal = summary.discountCents > 0 || summary.balanceAppliedCents > 0;

        return (
            <div className="ai-trial-upgrade__summary">
                <div className="ai-trial-upgrade__summary-title">Today&apos;s Billing Summary</div>
                <div className="ai-trial-upgrade__summary-table">
                    <div className="ai-trial-upgrade__summary-row ai-trial-upgrade__summary-row--head">
                        <span>Item</span>
                        <span>Amount</span>
                    </div>

                    {chargeLines.length > 0 ? (
                        chargeLines.map((line, index) => (
                            <div
                                className="ai-trial-upgrade__summary-row"
                                key={`charge-${index}-${line.description}`}
                            >
                                <span>{line.description || 'New subscription starting today'}</span>
                                <span>{formatMoney(line.amountCents, currency)}</span>
                            </div>
                        ))
                    ) : (
                        <div className="ai-trial-upgrade__summary-row">
                            <span>New subscription starting today</span>
                            <span>{recurringText}</span>
                        </div>
                    )}

                    <div className="ai-trial-upgrade__summary-row">
                        <span>AI Tracking, first 30 days</span>
                        <span>{formatMoney(0, currency)}</span>
                    </div>

                    {prorationLines.map((line, index) => (
                        <div
                            className="ai-trial-upgrade__summary-row"
                            key={`proration-${index}-${line.description}`}
                        >
                            <span>
                                {line.amountCents < 0
                                    ? 'Credit for unused time on current subscription'
                                    : 'Adjustment for the current billing period'}
                            </span>
                            <span
                                className={
                                    line.amountCents < 0
                                        ? 'ai-trial-upgrade__summary-credit'
                                        : undefined
                                }
                            >
                                {formatMoney(line.amountCents, currency)}
                            </span>
                        </div>
                    ))}

                    {showSubtotal && (
                        <div className="ai-trial-upgrade__summary-row">
                            <span>Subtotal</span>
                            <span>
                                {formatMoney(summary.subtotalCents - aiRecurringCents, currency)}
                            </span>
                        </div>
                    )}

                    {summary.discountCents > 0 && (
                        <div className="ai-trial-upgrade__summary-row">
                            <span>Discount applied</span>
                            <span className="ai-trial-upgrade__summary-credit">
                                {formatMoney(-summary.discountCents, currency)}
                            </span>
                        </div>
                    )}

                    {summary.balanceAppliedCents > 0 && (
                        <div className="ai-trial-upgrade__summary-row">
                            <span>Applied account balance</span>
                            <span className="ai-trial-upgrade__summary-credit">
                                {formatMoney(-summary.balanceAppliedCents, currency)}
                            </span>
                        </div>
                    )}

                    <div className="ai-trial-upgrade__summary-row ai-trial-upgrade__summary-row--total">
                        <span>Total charged today</span>
                        <span className="ai-trial-upgrade__summary-total">{totalText}</span>
                    </div>
                </div>
            </div>
        );
    }

    // The explanation has to match the invoice the user is actually looking at: a proration credit,
    // a discount, both, or neither.
    renderTip(summary) {
        const parts = [];
        if (summary.creditCents > 0) {
            parts.push('the unused-time credit from your current subscription');
        }
        if (summary.discountCents > 0) {
            parts.push('the discount applied to your account');
        }

        const text = parts.length
            ? `You are paying for your new subscription starting today, less ${parts.join(
                  ' and ',
              )}.`
            : 'You are paying for your new subscription starting today, which begins a fresh billing period.';

        return (
            <div className="ai-trial-upgrade__tip">
                <span className="ai-trial-upgrade__tip-icon">
                    <BulbIcon size={18} />
                </span>
                <p>{text}</p>
            </div>
        );
    }

    renderTiles({
        today,
        nextBillingDate,
        recurringText,
        aiTrialEndsDate,
        aiAfterTrialText,
        aiIntervalText,
    }) {
        const tiles = [
            // {
            //     icon: <CalendarIcon size={18} />,
            //     label: 'New billing cycle',
            //     value: `Starts today, ${formatDate(today)}`,
            // },
            // {
            //     icon: <CardIcon size={18} />,
            //     label: 'Next payment',
            //     value: `${recurringText} on ${formatDate(nextBillingDate)}`,
            // },
            {
                icon: <ClockIcon size={18} />,
                label: 'AI trial ends',
                value: formatDate(aiTrialEndsDate),
            },
            {
                icon: <RenewIcon size={18} />,
                label: 'After trial',
                value: `AI Tracking renews at ${aiAfterTrialText} ${aiIntervalText} unless canceled`,
            },
        ];
        return (
            <div className="ai-trial-upgrade__tiles">
                {tiles.map((tile) => (
                    <div className="ai-trial-upgrade__tile" key={tile.label}>
                        <div className="ai-trial-upgrade__tile-head">
                            <span className="ai-trial-upgrade__tile-icon">{tile.icon}</span>
                            <div className="ai-trial-upgrade__tile-label">{tile.label}</div>
                        </div>
                        <div className="ai-trial-upgrade__tile-value">{tile.value}</div>
                    </div>
                ))}
            </div>
        );
    }

    renderActions({ totalText, submitting }) {
        return (
            <div className="ai-trial-upgrade__actions">
                <button
                    type="button"
                    className="ai-trial-upgrade__confirm"
                    onClick={this.onConfirm}
                    disabled={submitting}
                >
                    {submitting && <Loading onlyPreloader />}
                    <LockIcon size={18} />
                    Confirm Update &amp; Pay {totalText}
                </button>
                <button
                    type="button"
                    className="ai-trial-upgrade__keep"
                    onClick={this.onKeep}
                    disabled={submitting}
                >
                    Keep My Current Subscription
                </button>
                <p className="ai-trial-upgrade__cancel-note">Cancel any time</p>
            </div>
        );
    }
}

export default AiTrialUpgrade;
